import {
  Container,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  List,
  ListItem,
  Spinner,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useSearchCompany } from "../lib/api/company.api";
import { useLogin } from "../lib/api/login.api";
import { useDebouncedState } from "../lib/utils/debounce.hooks";

export function LoginPage() {
  const { t } = useTranslation();
  const {
    value: searchTerm,
    debouncedValue: debouncedSearchTerm,
    setValue: setSearchTerm,
  } = useDebouncedState(500, "");
  const { data: companies, isLoading } = useSearchCompany(debouncedSearchTerm);
  const { mutate: login } = useLogin({
    onSuccess: async (_data, { loginRequest: { companyId } }) => {
      await navigate(`/verify/token?companyId=${companyId}`);
    },
    onError: () => {
      alert("Login failed");
    },
  });
  const navigate = useNavigate();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleCompanySelect = (companyId: number) => {
    login({ loginRequest: { companyId } });
  };

  return (
    <Container>
      <Flex direction="column" align="center">
        <Heading color="green.600" mb={10}>
          {t("login.heading")}
        </Heading>
        <FormControl>
          <FormLabel>{t("login.search_label")}</FormLabel>
          <Input
            placeholder={t("login.search_placeholder")}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <FormHelperText>{t("login.search_helper")}</FormHelperText>
        </FormControl>

        {isLoading ? (
          <Spinner />
        ) : (
          <List
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
            mt={3}
            maxH="20vh"
            h="20vh"
            w="full"
            py={2}
            px={1}
            overflowY="auto"
          >
            {companies?.map((company: { id: number; name: string }) => (
              <CompanyListItem
                key={company.id}
                onClick={() => handleCompanySelect(company.id)}
              >
                {company.name}
              </CompanyListItem>
            ))}
            {companies?.length === 0 && (
              <CompanyListItem noHoverEffect>
                {t("login.no_results")}
              </CompanyListItem>
            )}
          </List>
        )}
      </Flex>
    </Container>
  );
}

function CompanyListItem({
  children,
  noHoverEffect = false,
  ...props
}: {
  children: React.ReactNode;
  onClick?: () => void;
  noHoverEffect?: boolean;
}) {
  return (
    <ListItem
      {...props}
      sx={{
        borderRadius: "md",
        border: "1px",
        borderColor: "gray.200",
        width: "full",
        px: 2,
        py: 1,
        bg: "gray.50",
        _hover: noHoverEffect
          ? {}
          : {
              bg: "gray.100",
              cursor: "pointer",
            },
      }}
    >
      {children}
    </ListItem>
  );
}
